import { createUserWithEmailAndPassword, signInWithEmailAndPassword } from 'firebase/auth';
import { doc, getDoc, collection, query, where, getDocs } from 'firebase/firestore';
import { auth, db } from '../../firebase/config';

/**
 * Fonction de récupération pour synchroniser un compte administrateur existant dans Firestore
 * avec Firebase Authentication
 * @param {string} email - Email de l'administrateur
 * @param {string} password - Mot de passe à définir pour l'administrateur
 * @returns {Promise<Object>} Résultat de l'opération
 */
export const recoverAdminAccount = async (email, password) => {
  try {
    // Vérifier si l'utilisateur existe dans Firestore
    const emailQuery = query(collection(db, 'users'), where('email', '==', email));
    const emailSnapshot = await getDocs(emailQuery);
    
    if (emailSnapshot.empty) {
      return {
        success: false,
        error: "Aucun compte administrateur trouvé avec cet email dans la base de données."
      };
    }
    
    // Récupérer les données de l'utilisateur
    const userData = emailSnapshot.docs[0].data();
    const userId = emailSnapshot.docs[0].id;
    
    // Vérifier si l'utilisateur a un rôle d'administrateur
    if (userData.role !== 'admin' && userData.role !== 'PDG') {
      return {
        success: false,
        error: "Ce compte n'est pas un compte administrateur."
      };
    }
    
    // Essayer de créer un compte d'authentification
    try {
      // Tenter de créer un nouveau compte
      const userCredential = await createUserWithEmailAndPassword(auth, email, password);
      
      return {
        success: true,
        message: "Compte administrateur récupéré avec succès. Vous pouvez maintenant vous connecter.",
        userId: userCredential.user.uid
      };
    } catch (authError) {
      // Si l'email est déjà utilisé, essayer de se connecter
      if (authError.code === 'auth/email-already-in-use') {
        try {
          // Tenter de se connecter avec les identifiants fournis
          await signInWithEmailAndPassword(auth, email, password);
          
          return {
            success: true,
            message: "Connexion réussie. Le compte existe déjà dans le système d'authentification.",
            userId
          };
        } catch (signInError) {
          return {
            success: false,
            error: "Le compte existe déjà dans le système d'authentification mais le mot de passe est incorrect.",
            code: signInError.code
          };
        }
      }
      
      // Autres erreurs d'authentification
      return {
        success: false,
        error: `Erreur lors de la récupération du compte: ${authError.message}`,
        code: authError.code
      };
    }
  } catch (error) {
    console.error('Erreur lors de la récupération du compte administrateur:', error);
    return {
      success: false,
      error: `Une erreur est survenue: ${error.message}`,
      code: error.code
    };
  }
};

/**
 * Vérifie si un compte administrateur existe dans Firestore mais pas dans Authentication
 * @param {string} email - Email de l'administrateur à vérifier
 * @returns {Promise<Object>} Résultat de la vérification
 */
export const checkAdminAccountStatus = async (email) => {
  try {
    // Vérifier si l'utilisateur existe dans Firestore
    const emailQuery = query(collection(db, 'users'), where('email', '==', email));
    const emailSnapshot = await getDocs(emailQuery);
    
    if (emailSnapshot.empty) {
      return {
        exists: false,
        message: "Aucun compte administrateur trouvé avec cet email."
      };
    }
    
    // L'utilisateur existe dans Firestore
    const userData = emailSnapshot.docs[0].data();
    
    // Vérifier si c'est un administrateur
    if (userData.role !== 'admin' && userData.role !== 'PDG') {
      return {
        exists: true,
        isAdmin: false,
        message: "Ce compte existe mais n'est pas un compte administrateur."
      };
    }
    
    return {
      exists: true,
      isAdmin: true,
      userId: emailSnapshot.docs[0].id,
      userData,
      message: "Compte administrateur trouvé dans la base de données."
    };
  } catch (error) {
    console.error('Erreur lors de la vérification du compte:', error);
    return {
      error: true,
      message: `Une erreur est survenue: ${error.message}`
    };
  }
};